import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Textfit } from "react-textfit";
import { get } from "lodash";

import ReportContainer from "../ReportContainer";
import CellGroup from "../CellGroup";
import GradeHeader from "../GradeHeader";
import SkillModal from "../SkillModal";
import Loading from "../../../Global/Loading";
import { domainInfo } from "../../util/domainInfo";
import getPeriods from "../../util/getPeriods";
import Tooltip from "rc-tooltip";

import "./index.scss";
import "rc-tooltip/assets/bootstrap_white.css";
import cx from "classnames";
import getModuleKeyFromSkill from "./functions/getModuleKeyFromSkill";
import getPeriodDomainWidths from "./functions/getPeriodDomainWidths";
import { GetBrandedAppName } from "../../../../services/BrandingService";
import defaultCurriculumConfig from "../ReportSettings/SelectSkillsCurriculum/defaultCurriculumConfig";
import { useTranslation } from "react-i18next";

const allPeriods = getPeriods();

export default function SkillsDiagnostic(props) {
  const roster = useSelector((state) => state.reports.roster);
  const selectedCurriculum = useSelector((state) => state?.reports?.teacher?.selectedCurriculum?.value);
  const locale = useSelector(state => state?.reports?.teacher?.selectedLocale);
  const studentId = get(roster, "ui.selections.student.value", null);
  const [isErssStudent, setIsErssStudent] = useState(false);
  const report = useSelector((state) => state.reports.skillsdiagnostic.report);
  const status = get(report, "status.type", null);
  const data = get(report, "data", null);
  const [skillModalData, setSkillModalData] = useState(null);

  const {t} = useTranslation("reading_rope_report");

  useEffect(() => {
    props.initializeData();
  }, []);

  // Check for displayErssStatus in the first Domain to know if there's at least 1 current/InProgress
  // subSkill. If this is false we would hide the Checks and Highlight/AmiraLogo.
  useEffect(() => {
    Array.isArray(data) && setIsErssStudent(data[0] && data[0].displayErssStatus);
  }, [data]);

  // close skill modal when switching students
  useEffect(() => {
    setSkillModalData(null);
  }, [studentId]);

  /**
   * Domain / Period / Module / Sub-Module
   */

  const {
    maxWidthForEachModuleAcrossDomains, // pre-calculate the width of each module across all domains
    maxWidthPercentageForEachModuleSubSkills, // pre-calculate the percentage width of each subSkillGroup across all domains, used to align subSkillGroups
    allSubSkillGroupsForEachModuleAcrossDomains, // create a list of all subSkillGroups for each ERSS module for rendering in groups
    periodWidths, // pre-calculate the width of each period / domain container (align)
    widthsReady, // flag to indicate if the pre-calculations are complete
  } = useMemo(() => {
    const maxWidthForEachModuleAcrossDomains = {};
    const maxWidthPercentageForEachModuleSubSkills = {};
    const allSubSkillGroupsForEachModuleAcrossDomains = {};
    const periodWidths = getPeriodDomainWidths(allPeriods, data);
    let widthsReady = false;

    if (data) {
      const periodDomainWidths = {};

      data.forEach((domainData) => {
        const { progression, domain } = domainData;
        progression.forEach((skill) => {
          const { subSkills, gradeLabel } = skill;
          const period = gradeLabel;
          
          let periodSkills = progression.filter((x) => x.gradeLabel === period);
          let sumOfPeriodModulesWidth = 0;
          for (const skill of periodSkills) {
            sumOfPeriodModulesWidth += skill.width;
          }
  
          // create a list of all subSkillGroups for each module
          const subSkillGroups = subSkills.reduce((acc, subSkill) => {
            const {sequence} = subSkill;
            const subModuleName = sequence
            if(!subModuleName) return acc;
            if(!acc.includes(subModuleName)) {
              acc.push(subModuleName)
            }
            return acc
          }, [])
  
          const module = getModuleKeyFromSkill(skill)
          const periodModule = period + " " + module;
  
          const constant = Math.floor(
            (periodWidths[period] - 3) / sumOfPeriodModulesWidth
          );
          const width = skill.width * constant;
  
          maxWidthForEachModuleAcrossDomains[periodModule] = Math.max(
            maxWidthForEachModuleAcrossDomains[periodModule] || 0,
            width
          );
  
          // keep track of the max width for each period / domain container (the box that contains all modules for a period in a domain)
          periodDomainWidths[period] = {
            ...periodDomainWidths[period],
            [domain]: periodDomainWidths[period]?.[domain] || 0 + maxWidthForEachModuleAcrossDomains[periodModule] * periodSkills.length
          }
  
          // update period widths if the current domain width is greater than pre calculated period width
          if(periodDomainWidths[period]?.[domain] > periodWidths[period]) {
            periodWidths[period] = periodDomainWidths[period]?.[domain]
          }
  
          // create the subSkillGroups object and count the number of subSkills in each groups
          subSkills.forEach((subSkill) => {
            const {sequence} = subSkill;
            const subModuleName = sequence
  
            // create a list of all subSkillGroups for each module
            allSubSkillGroupsForEachModuleAcrossDomains[module] = {
              count: Math.max(
                allSubSkillGroupsForEachModuleAcrossDomains[module]?.count || 1,
                Object.keys(subSkillGroups).length
              ),
              groups: [
                ...allSubSkillGroupsForEachModuleAcrossDomains[module]?.groups || [],
                ...(allSubSkillGroupsForEachModuleAcrossDomains[module]?.groups?.includes(subModuleName) ? [] : [subModuleName])
              ]
            }
  
            if(!subModuleName) return;
  
            // calculate the width of the subModule based on the number of subskill groups
            // we are using the largest number of subskills in a module (across all domains) as the denominator for the percentage
            const currentSubSkillCount = subSkills?.filter((x) => x.sequence === subModuleName).length || 1;
            const percentWidth = allSubSkillGroupsForEachModuleAcrossDomains[module]?.count || 1 > currentSubSkillCount ?
              100 / allSubSkillGroupsForEachModuleAcrossDomains[module]?.count || 1 :
              100 / currentSubSkillCount;
            
            
            // count is the number of subskills with the same name
            const count = Math.max(
              maxWidthPercentageForEachModuleSubSkills[subModuleName]?.count || 1,
              currentSubSkillCount
            );

            const MIN_WIDTH = 22;

            const pixelWidth = currentSubSkillCount > count ? 
              currentSubSkillCount * MIN_WIDTH : 
              count * MIN_WIDTH;
  
            maxWidthPercentageForEachModuleSubSkills[subModuleName] = {
              pixelWidth,
              percentWidth,
              count,
            };

            // list of submodules for the current module
            const subSkillGroupsForCurrentModule = allSubSkillGroupsForEachModuleAcrossDomains[module]?.groups || [];
            // sum of the widths of all submodules for the current module
            const sumOfSubSkillGroupsWidthsForCurrentModule = subSkillGroupsForCurrentModule.reduce((acc, subSkillGroup) => {
              return acc + maxWidthPercentageForEachModuleSubSkills[subSkillGroup]?.pixelWidth
            }, 0)

            // update maxWidthForEachModuleAcrossDomains for the current module
            // maxWidthForEachModuleAcrossDomains[periodModule] = Math.max(
            //   maxWidthForEachModuleAcrossDomains[periodModule] || 0,
            //   sumOfSubSkillGroupsWidthsForCurrentModule
            // )
            maxWidthForEachModuleAcrossDomains[periodModule] = sumOfSubSkillGroupsWidthsForCurrentModule

            // end calculation
          });
        });
      });

      widthsReady = true;
    }

    return {
      maxWidthForEachModuleAcrossDomains,
      maxWidthPercentageForEachModuleSubSkills,
      allSubSkillGroupsForEachModuleAcrossDomains,
      periodWidths,
      widthsReady,
    };
  }, [data]);

  const activePeriods = Object.keys(periodWidths);

  return (
    <ReportContainer>
      <SkillModal
        data={skillModalData}
        onClose={() => setSkillModalData(null)}
      />
      <div className="skills-diagnostic">{renderContent()}</div>
    </ReportContainer>
  );

  function renderContent() {
    if (status === "ERROR")
      return (
        <div className="filler-text">
          {t("skills_status_report:fetch_error")}
        </div>
      );

    if (!studentId)
      return <div className="filler-text">Please select a student.</div>;

    if (!data || status === "PENDING" || !widthsReady){
      
      return (
        <div style={{ height: 500 }}>
          <Loading />
        </div>
      );
    }

    return (
      <React.Fragment>
        <GradeHeader periodWidths={periodWidths} buffer={140} />
        <div className="skills-table-body" style={{ display: "flex" }}>
          <div>
            {data.map((domain, index) => {
              const last = index === data.length - 1;
              return <div className="skills-row-container" key={index}>{renderDomainRow(domain, last, isErssStudent)}</div>;
            })}
          </div>
        </div>
      </React.Fragment>
    );
  }

  function renderDomainRow(domain, last, isErssStudent) {
    const { progression } = domain;
    const { color, name } = domainInfo[domain.domain];
    var domainPeriodClass = cx({
      period: true,
      "not-last": !last,
    });
    const isDefaultCurriculum = selectedCurriculum === defaultCurriculumConfig[locale].curriculumId || !selectedCurriculum;

    const isERSSDomain = isDefaultCurriculum && isErssStudent && ["Phonological Awareness", "Decoding", "High Frequency Words"].includes(name);
    const domainRowClass = cx({
      "domain-row": true,
      center: true,
      "domain-row-with-progress-icon": isERSSDomain,
    });

    return (
      <div className={domainRowClass}>
        <Tooltip
          placement="bottom"
          trigger={domain.domain === "STRUCTURE_REASONING" ? ["hover"] : []}
          overlayStyle={{ width: 200, background: "white" }}
          overlay={
            <p style={{ textAlign: "center" }}>
              {t("common:sr_coming_soon")}
            </p>
          }
        >
          <div className="domain-name" style={{ color }}>
            {t(name)}
          </div>
        </Tooltip>
        {activePeriods.map((period, i) => {
          const periodSkills = progression.filter(
            (x) => x.gradeLabel === period
          );
          let sumOfPeriodModulesWidth = 0;
          for (const skill of periodSkills) {
            sumOfPeriodModulesWidth += skill.width;
          }
          return (
            <div
              key={i}
              className={domainPeriodClass}
              style={{
                maxWidth: periodWidths[period],
                minWidth: periodWidths[period],
              }}
            >
              {periodSkills.map((skill, index) => {
                const module = getModuleKeyFromSkill(skill)
                const periodModule = period + " " + module;
                const allSubSkillsAreERSS = skill.subSkills.every((subSkill) => subSkill.sequence);

                const constant = Math.floor(
                  (periodWidths[period] - 3) / sumOfPeriodModulesWidth
                );
                const width = skill.width * constant;

                const effectiveWidth = (allSubSkillsAreERSS && isDefaultCurriculum && !module.includes('Wilson F')) ? maxWidthForEachModuleAcrossDomains[periodModule] : width;

                return (
                  <div
                    key={index}
                    className={`progression ${skill.gradeLabel} module ${module} ${`erss-domain-${isERSSDomain}`}`}
                    style={{ width: effectiveWidth, minWidth: effectiveWidth, maxWidth: effectiveWidth }}
                  >
                    <div
                      className="skillname-box center"
                      style={{ background: color }}
                    >
                      <Textfit
                        mode="multi"
                        forceSingleModeWidth={false}
                        max={16}
                      >
                        <div className="skillname-text center">
                          {skill.skillName}
                        </div>
                      </Textfit>
                    </div>

                    <div style={{ display: "flex" }}>
                      <CellGroup
                        module={module}
                        isDefaultCurriculum={isDefaultCurriculum}
                        displayErssStatus={isERSSDomain}
                        maxWidthPercentageForEachModuleSubSkills={maxWidthPercentageForEachModuleSubSkills}
                        allSubSkillGroupsForEachModuleAcrossDomains={allSubSkillGroupsForEachModuleAcrossDomains}
                        currentSkillCount={skill.currentSkillCount}
                        subSkills={skill.subSkills}
                        domain={domain.domain}
                        skillArea={skill.skillName}
                        height={30}
                        setSkillModalData={setSkillModalData}
                        studentId={studentId}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }
}
